import i18n from '../i18n';

SelfScheduleStore.dispatchToken = MakeShiftEmployeeDispatcher.register(
  SelfScheduleStore.dispatchCallback,
);

// /api/v3/user's company.enable_self_scheduling is the company's gate: its own setting and the
// feature's Flipper release. An account cached before the backend sent the flag has none, which
// reads as on.
SelfScheduleStore.isEnabledForCompany = function () {
  const coreUser = Utils.getCoreUser();
  const company = coreUser && coreUser.company;
  return !!company && company.enable_self_scheduling !== false;
};

// The employee's window times arrive as UTC instants. Read them in the block's time_zone, its
// department's, so they share a wall clock with the block's shifts; without one they fall back to
// the browser's zone.
const blockTime = (block, value) => (
  block.time_zone ? moment(value).tz(block.time_zone) : moment(value)
);

// The menu's status line (MS-5709). An employee sees a block as either open or closed, so this
// follows their own window rather than the block's status: when it opens, then when it shuts,
// then under review once it has. A group window with no end of its own runs to the block's close.
// Closed and balancing read as under review whatever the windows say, since after an early close
// they can still be ahead. Null for an employee in no pick group on an open block, and for a
// window with no end at all.
const accessLabelOf = (block) => {
  const endsAt = block.access_ends_at || block.closes_at;
  if (block.status !== 'open' || (endsAt && !moment(endsAt).isAfter(moment()))) {
    return i18n.t('HeaderBar.closed_under_review');
  }
  if (!block.access_starts_at) {
    return null;
  }
  const starts = blockTime(block, block.access_starts_at);
  const upcoming = starts.isAfter(moment());
  if (!upcoming && !endsAt) {
    return null;
  }
  const at = upcoming ? starts : blockTime(block, endsAt);
  return i18n.t(upcoming ? 'HeaderBar.access_starts_at' : 'HeaderBar.closes_at', {
    date: Utils.formatLocaleShortDate(at),
    time: at.format('h:mm a'),
  });
};

// The three lines a block reads as in the Self Schedule menu, in the mobile app's order.
const labelBlock = block => Object.assign({}, block, {
  label: Utils.formatLocaleDateRange(block.block_start_date, block.block_end_date),
  locationLabel: _.compact([block.location_name, block.department_name]).join(' \u2022 '),
  accessLabel: accessLabelOf(block),
});

// Open blocks, and closed or balancing ones read-only while under review (MS-5709). A block is
// retired once published or once its period's end date has passed, whichever comes first
// (MS-5710). The fetch leaves out published blocks; this drops one whose end date is behind its
// department's today, which the fetch's slack lets through and a page left open overnight keeps.
SelfScheduleStore.getBlocks = function () {
  return SelfScheduleStore.present()
    .filter(block => blockTime(block, moment()).format('YYYY-MM-DD') <= block.block_end_date)
    .map(labelBlock);
};

SelfScheduleStore.onBlocksHandler = function (collection, response) {
  SelfScheduleStore.trigger('self_schedule:blocks:load:success', collection, response);
};

// ---------------------------------------------------------------------------------------------
// One block's page, resolved once per fetch into everything the page renders from. Windows open
// and close on the clock with no write, so every read recomputes the lot; the page refetches after
// each write for the same reason.
// ---------------------------------------------------------------------------------------------

// A shift requirement's times arrive as UTC instants plus, because we send X-Localize-Times, an
// `_in_zone` twin carrying the department's offset. parseZone keeps that offset instead of
// converting to the browser's, so a shift reads at the wall clock the department works to.
const requirementTime = (shift, key) => {
  const localized = shift[`${key}_in_zone`];
  return localized ? moment.parseZone(localized) : moment(shift[key]);
};

// The calendar chip's short form (`label`) and the sidebar's full one (`time`). `end` is null when
// the shift hides its end, which reads TBD the way the schedule page shows it.
const timeLabels = (start, end) => {
  const from = start.format('h:mm a');
  const to = end ? end.format('h:mm a') : i18n.t('ScheduleDay.tbd');
  return {
    label: `${Utils.formatTimeString(from)} – ${Utils.formatTimeString(to)}`,
    time: `${from} – ${to}`,
  };
};

// A pool row with what the calendar and sidebar branch on already decided. Severity comes from
// the resolved enforcement, never the warning code: a department can make any of them a hard stop
// or an aside, and a hard stop on selection renders visible-but-unselectable rather than being
// rejected after the click.
const describeShift = (shift) => {
  const warnings = shift.warnings || [];

  return Object.assign({}, shift, timeLabels(
    requirementTime(shift, 'starts_at'),
    shift.hide_ends_at ? null : requirementTime(shift, 'ends_at'),
  ), {
    held: !!shift.my_request_id,
    blocked: warnings.some(w => w.enforcement === 'hard_stop_on_selection'),
    warned: warnings.some(w => w.enforcement !== 'hard_stop_on_selection'),
  });
};

// A shift the employee already works, from ShiftStore: it carries its own time_zone, the way the
// schedule page reads it, and is keyed by the local date of its start.
SelfScheduleStore.describeRosterShift = function (shift) {
  const start = moment(shift.start_time).tz(shift.time_zone);

  return Object.assign({}, shift, { date: start.format('YYYY-MM-DD') }, timeLabels(
    start,
    shift.hide_end_time ? null : moment(shift.end_time).tz(shift.time_zone),
  ));
};

// A block N/A entry is the underlying unavailable with its type nested. The schedule page's chip
// and row read `na` and `colour` flat, the way /api/v3/unavailables sends them, so lay them out
// that way; both are the API's own verdict on whether the day is still a live N/A day. The nested
// type stays on the row, which is where its name is read from.
const describeNaEntry = (entry) => {
  const type = entry.unavailable_type || {};
  return Object.assign({}, entry, {
    unavailable_type_id: type.id, na: !!type.na, colour: type.colour,
  });
};

// A submit check's or a counter row's obligations as one list: the block's own total_hours, then
// the FTE model's rules, in the API's order (a summary stored before the FTE split carries them
// all under `obligations`).
const obligationsOf = row => (row.obligations || []).concat((row.fte && row.fte.obligations) || []);

// The FTE Obligation Model's Day, Night and Weekend counters, read the way both mobile apps read
// them: only where the model runs for the block's department, always all three and in that order,
// each the obligation of its core kind on the block's own department row, whatever order the API
// sends them in. A kind with no obligation (never set up, or an employee the model bypasses) has
// no target. Its actual is still the department's classification figure, which is what the
// obligation would have measured; the mobile apps read 0 there.
const FTE_KINDS = { day_shift: 'day_hours', night_shift: 'night_hours', weekend_shift: 'weekend_count' };

const describeFteCounters = (counters) => {
  if (!counters || !counters.fte_engine_available) {
    return [];
  }

  const row = counters.counters || {};
  const obligations = obligationsOf(row);
  const classification = (row.fte && row.fte.classification) || {};
  return _.map(FTE_KINDS, (figure, kind) => (
    _.findWhere(obligations, { core_kind: kind })
      || { core_kind: kind, actual: classification[figure] || 0, target: null }
  ));
};

// The three shut states read differently to an employee, so the messaging branches on this rather
// than on block.status alone.
const windowStateOf = (block, groupWindows) => {
  if (groupWindows.length === 0) {
    return 'not_assigned';
  }
  if (block.access_window_open && groupWindows.some(w => w.access_window_open)) {
    return 'open';
  }
  // A closed or balancing block is shut whatever its group windows say: after an early close
  // they can still be ahead.
  return block.status === 'open' && groupWindows.some(w => w.access_window_upcoming)
    ? 'upcoming'
    : 'ended';
};

// While picking is open, eligible_shifts is the right list: the API has already dropped everything
// this employee may not take, and rows they already hold come back marked. Once their window shuts
// that list goes empty, so fall back to their own live selections resolved against the block's
// locked pool. Publishing turns every live selection into a real shift on the roster overlay, so
// rendering the selections as well would double every chip on the month.
const shiftsOf = (block, canPick, results) => {
  if (canPick) {
    // The one read allowed to fail: eligible_shifts is a 403 for a department admin who is
    // read-only on the schedule, even though it is a read.
    return results.eligible.ok ? results.eligible.rows : [];
  }
  if (block.status === 'published') {
    return [];
  }

  const poolById = _.indexBy(results.block.included, 'id');
  return results.requests.rows
    .filter(request => request.status === 'pending' || request.status === 'approved')
    .map(request => poolById[request.shift_requirement_id] && Object.assign(
      {}, poolById[request.shift_requirement_id], {
        my_request_id: request.id, my_request_status: request.status, warnings: [],
      },
    ))
    .filter(Boolean);
};

SelfScheduleStore.onBlockDataHandler = function (blockId, results) {
  const failed = _.find(_.omit(results, 'eligible'), result => !result.ok);
  if (failed) {
    SelfScheduleStore.trigger('self_schedule:block:load:error', blockId, failed);
    return;
  }

  const block = labelBlock(results.block.rows[0]);
  const groupWindows = results.groups.rows;
  const windowState = windowStateOf(block, groupWindows);
  const canPick = block.status === 'open' && windowState === 'open';
  const shifts = shiftsOf(block, canPick, results).map(describeShift);
  // The earliest start across the employee's own group windows, for the "opens at" message.
  const windowStarts = groupWindows.map(w => w.access_starts_at).filter(Boolean).sort();
  // An employee has no submission at all until their first selection creates one.
  const submission = results.submissions.rows[0] || null;
  const progress = results.progress.rows[0];

  SelfScheduleStore.trigger('self_schedule:block:load:success', {
    blockId,
    block,
    windowState,
    windowOpensAt: windowStarts[0] ? blockTime(block, windowStarts[0]) : null,
    // The one gate on every change, Request, Cancel, Submit and Reopen alike (MS-5709): an
    // employee can act on a block while their own window is open and on nothing once it shuts.
    // ponytail: the API still takes a cancel while the block's window outlasts the group's, and
    // after close during a negotiation window. Neither is offered; a manager-gated reopen is the
    // card's deferred answer to post-close drops.
    canPick,
    published: block.status === 'published',
    shifts,
    // What the legend counts. Read off the eligible list itself rather than `shifts`, which falls
    // back to the employee's own selections once that list goes empty.
    eligibleCount: results.eligible.ok ? results.eligible.rows.length : 0,
    // The employee's live selections in calendar order. Assembled here rather than read off
    // submission.validation_summary.tentative_block, which is only populated between a submit
    // attempt and the next reopen.
    picks: _.sortBy(shifts.filter(shift => shift.held), shift => `${shift.date}${shift.starts_at}`),
    progress,
    fteCounters: describeFteCounters(progress.counters),
    submission,
    // Days the employee has marked N/A inside the block, still listed once N/A Days is turned off.
    naEntries: results.naEntries.rows.map(describeNaEntry),
  });
};

SelfScheduleStore.onWriteSuccess = function (blockId) {
  SelfScheduleStore.trigger('self_schedule:write:success', blockId);
};

// The submit check, read the way the iOS app reads it. Every obligation the check measured is a
// row. A finding whose rule is a row's key repeats that row's sentence word for word, so only the
// rest (the window, completeness and no-target findings) are said apart from the rows. iOS runs
// those together in one callout; here errors stay apart from warnings, because an error is what
// stopped the submit. A refused submit and a stored summary carry the check in the same shape, and
// a summary the check never filled (`{}` before the first submit and after a reopen) has no
// `errors` and so no check.
SelfScheduleStore.describeCheck = function (summary) {
  if (!summary || !summary.errors) {
    return null;
  }

  const obligations = obligationsOf(summary);
  const keys = _.pluck(obligations, 'key');
  const unsaid = findings => (findings || [])
    .filter(finding => !_.contains(keys, finding.rule))
    .map(finding => finding.message);

  return { obligations, errors: unsaid(summary.errors), warnings: unsaid(summary.warnings) };
};

// A refused write, in the words the API chose. A submit the check blocked (submission_blocked)
// sends the whole check under meta.data and carries that check rather than sentences. A rejected
// selection sends its reasons as meta.data.errors, and anything else has only the error's own
// detail.
SelfScheduleStore.onWriteError = function (blockId, errors) {
  const findings = (errors[0] && errors[0].meta && errors[0].meta.data) || {};
  const check = errors[0] && errors[0].code === 'submission_blocked'
    ? SelfScheduleStore.describeCheck(findings)
    : null;
  const detailed = findings.errors && findings.errors.length > 0
    ? findings.errors.map(finding => finding.message)
    : errors.map(error => error.detail);

  SelfScheduleStore.trigger('self_schedule:write:error', blockId, {
    messages: check ? [] : _.uniq(_.compact(detailed)),
    check,
  });
};

// Utils.signOut() is a client-side redirect, not a page reload, so this collection would otherwise
// survive into the next session and briefly show the previous user's blocks.
AccountStore.on('account:logout', () => {
  SelfScheduleStore.reset();
});
